import React from 'react';
import { Heart, Gift, Clock, HandHeart, Users, CircleDollarSign, ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const DonationOption = ({ icon: Icon, title, description, items }) => (
    <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500
                    transform hover:-translate-y-2 border border-purple-100/20
                    group relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br
                       from-purple-100 to-purple-50/50 rounded-full
                       group-hover:scale-150 transition-transform duration-700"/>
        <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-purple-50
                       rounded-full group-hover:scale-150 transition-transform duration-700"/>

        <div className="relative z-10">
            <div className="inline-flex mb-6 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-500
                               rounded-xl transform rotate-3 group-hover:rotate-6
                               transition-all duration-500 blur-[2px]"></div>
                <div className="relative p-4 bg-gradient-to-r from-purple-600 to-purple-500
                               rounded-xl transform rotate-3 group-hover:rotate-6
                               transition-all duration-500">
                    <Icon className="h-7 w-7 text-white transform -rotate-3
                                   group-hover:-rotate-6 transition-all duration-500" />
                </div>
            </div>
            <h3 className="text-2xl font-bold mb-3 text-gray-900">{title}</h3>
            <p className="text-gray-600 mb-6">{description}</p>
            {items && (
                <ul className="space-y-3">
                    {items.map((item, index) => (
                        <li key={index} className="flex items-center text-gray-600 group">
                            <Star className="h-4 w-4 text-purple-500 mr-3 flex-shrink-0
                                          group-hover:rotate-12 group-hover:scale-110
                                          transition-all duration-300" />
                            <span className="group-hover:text-purple-700 transition-colors duration-300">
                                {item}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    </div>
);

const SupportPage = () => {
    const donationOptions = [
        {
            icon: Gift,
            title: "Material Donations",
            description: "We welcome new and gently used items to support our community:",
            items: [
                "Clothing and Shoes",
                "Personal hygiene products",
                "Office/school supplies",
                "Appliances",
                "Toys",
                "Furniture",
                "Non-perishable food items"
            ]
        },
        {
            icon: CircleDollarSign,
            title: "Financial Support",
            description: "Your generous contributions help us serve more families:",
            items: [
                "One-time donations",
                "Monthly giving programs",
                "Sponsor a family",
                "Emergency assistance fund",
                "Back-to-school campaign"
            ]
        },
        {
            icon: HandHeart,
            title: "Volunteer With Us",
            description: "Share your time and talents to make a difference:",
            items: [
                "Monthly distribution events",
                "Fundraising activities",
                "Administrative support",
                "Community outreach",
                "Special events"
            ]
        }
    ];

    return (
        <div className="flex flex-col min-h-screen">
            {/* Hero Section */}
            <section className="relative h-[60vh] flex items-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={require("../assets/images/charity4.jpg")}
                        alt="Support Our Mission"
                        className="w-full h-full object-cover object-center scale-105
                                 motion-safe:animate-[ken-burns_20s_ease-in-out_infinite_alternate]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-purple-900/40 to-black/80" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center space-y-8">
                        <div className="inline-flex items-center px-4 py-2 rounded-full
                                      bg-white/10 backdrop-blur-sm border border-white/20">
                            <Heart className="h-5 w-5 text-purple-300 mr-2" />
                            <span className="text-purple-100 font-medium">Make a Difference Today</span>
                        </div>
                        <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight">
                            Support Our
                            <span className="block mt-2 bg-gradient-to-r from-purple-200 to-white
                                         text-transparent bg-clip-text">
                                Mission
                            </span>
                        </h1>
                        <p className="text-xl text-purple-100 max-w-2xl mx-auto leading-relaxed">
                            Join us in making a lasting impact in our community through your
                            generous support and involvement.
                        </p>
                    </div>
                </div>
            </section>

            {/* Donation Options */}
            <section className="py-24 bg-gradient-to-b from-white to-purple-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16 space-y-4">
                        <h2 className="text-4xl font-bold text-gray-900">Ways to Help</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Every contribution, whether time, items, or financial support,
                            helps us continue our mission of serving those in need.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {donationOptions.map((option, index) => (
                            <DonationOption key={index} {...option} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Donation Section */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-purple-50 to-white" />
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 relative overflow-hidden
                                  border border-purple-100/20">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-transparent" />
                        <div className="relative z-10">
                            <div className="text-center mb-8 space-y-4">
                                <h2 className="text-3xl font-bold text-gray-900">Make a Donation</h2>
                                <p className="text-gray-600">
                                    Support a family through Community Charitable Ministry
                                </p>
                            </div>
                            <div className="space-y-6">
                                <a href="mailto:communitycharitableministry@gmail.com?subject=Donation%20enquiry"
                                   className="block w-full bg-purple-600 text-white text-center
                                            px-6 py-4 rounded-xl font-semibold text-lg
                                            hover:bg-purple-700 transition-all duration-300
                                            transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
                                >
                                    Contact Us to Donate
                                </a>
                                <p className="text-sm text-gray-500 text-center">
                                    Email the ministry to arrange your donation, or use the bank details below.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bank Donation Section */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mt-12">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Make a Donation</h2>
                        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                            <div className="border-b pb-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">CCM Account</h3>
                                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Bank</dt>
                                        <dd className="mt-1 text-sm text-gray-900">ECAB</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Account Name</dt>
                                        <dd className="mt-1 text-sm text-gray-900">Community Charitable Ministry</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Nick Name</dt>
                                        <dd className="mt-1 text-sm text-gray-900">CCM</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Account Number</dt>
                                        <dd className="mt-1 text-sm text-gray-900">400269535</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Mobile</dt>
                                        <dd className="mt-1 text-sm text-gray-900">268 561-1573</dd>
                                    </div>
                                </dl>
                            </div>

                            <div className="border-b pb-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">CCM/ABCSC Account</h3>
                                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Bank</dt>
                                        <dd className="mt-1 text-sm text-gray-900">ECAB</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Account Name</dt>
                                        <dd className="mt-1 text-sm text-gray-900">Community Charitable Ministry</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Nick Name</dt>
                                        <dd className="mt-1 text-sm text-gray-900">CCM/ABCSC</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Account Number</dt>
                                        <dd className="mt-1 text-sm text-gray-900">102001308</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Mobile</dt>
                                        <dd className="mt-1 text-sm text-gray-900">268 561-1573</dd>
                                    </div>
                                </dl>
                            </div>

                            <div className="pt-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Important Notes:</h4>
                                <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                                    <li>Please include "Dues" or "Donation" as your transaction reference</li>
                                    <li>Keep your transaction receipt for your records</li>
                                    <li>Contact us if you need any assistance with your donation</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Additional Information */}
            <section className="py-24 bg-gradient-to-br from-purple-900 to-purple-800 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl
                                      border border-white/10 hover:border-white/20
                                      transition-colors duration-300">
                            <h3 className="text-2xl font-bold text-white mb-4">
                                Monthly Distribution
                            </h3>
                            <p className="text-purple-100 mb-6">
                                We welcome donations of items on the last Saturday of every month.
                                All contributions help us support families in need.
                            </p>
                            <div className="flex items-center space-x-2 text-purple-200">
                                <Clock className="h-5 w-5" />
                                <span>Last Saturday of every month</span>
                            </div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl
                                      border border-white/10 hover:border-white/20
                                      transition-colors duration-300">
                            <h3 className="text-2xl font-bold text-white mb-4">
                                Join Our Team
                            </h3>
                            <p className="text-purple-100 mb-6">
                                Become part of our volunteer family and help make a meaningful
                                difference in our community.
                            </p>
                            <Link
                                to="/contact"
                                className="inline-flex items-center px-6 py-3 rounded-xl
                                         bg-white text-purple-900 font-semibold
                                         hover:bg-purple-50 transition-all duration-300
                                         group transform hover:-translate-y-1"
                            >
                                Contact Us
                                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1
                                                   transition-transform duration-300" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SupportPage;
